import { isValidElement, type ReactElement } from "react";
import Link from "next/link";
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Tap-target floor on coarse pointers (touch devices): every button is at
// least 44px square there, while mouse-driven layouts keep the compact sizes.
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-label in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonProps = ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /**
     * Whether `render` produces a link. Left unset, it is detected: a plain
     * `<a>`, a Next `<Link>`, or any element given an `href`. Set it for what
     * cannot be seen from outside, such as a render function that returns an
     * anchor, or `false` to keep button semantics on an element with an href.
     */
    link?: boolean;
  };

/** Whether `render` is a link element, as far as it can be told from outside. */
function detectLink(render: ButtonPrimitive.Props["render"]): boolean {
  if (!isValidElement(render)) return false;
  const { type, props } = render as ReactElement<{ href?: unknown }>;
  return type === "a" || type === Link || props.href != null;
}

function Button({
  className,
  variant = "default",
  size = "default",
  render,
  nativeButton,
  link,
  ...props
}: ButtonProps) {
  // Base UI makes every non-<button> role="button" and activates it on Space,
  // which turns a link into a button for a screen reader and makes Space follow
  // it instead of scrolling. A link keeps its own role and native keys: Enter
  // follows it, Space scrolls. The explicit undefined role overrides the one
  // Base UI adds, and preventBaseUIHandler skips its synthetic key handling.
  // Disabled stays Base UI's: aria-disabled, no tab stop, and a prevented click
  // (which Enter on a focused anchor also produces).
  const isLink = link ?? detectLink(render);
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      render={render}
      nativeButton={isLink ? false : nativeButton}
      {...(isLink ? { role: undefined } : null)}
      {...props}
      {...(isLink
        ? {
            onKeyDown(event) {
              props.onKeyDown?.(event);
              event.preventBaseUIHandler();
            },
            onKeyUp(event) {
              props.onKeyUp?.(event);
              event.preventBaseUIHandler();
            },
          }
        : null)}
    />
  );
}

export { Button, buttonVariants };
